import PropTypes from 'prop-types';
import React, { Suspense } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';

import { finalizeBillingConversation } from '../../actions/conversations';
import { fetchPaymentMethods } from '../../actions/paymentMethods';
import { fetchUser as fetchUserAction } from '../../actions/user';
import { fetchTransferts as fetchTransfertsAction } from '../../actions/transferts';

import i18n from '../../i18n';

import Conversation from '../../models/Conversation';
import PaymentMethod from '../../models/PaymentMethod';
import User from '../../models/User';
import PaymentModel from '../../models/Payment';

import { withCurrency } from '../currency';
import Loading from '../common/Loading';
import Analytics from '../../utils/analytics';
import useGTM from '../analytics/useGTM';
import { fetchBillingAddress } from '../../actions/billingAddress';
import { fetchLegalEntityAssignment, fetchLegalEntityCountries } from '../../actions/legalEntity';

const Billing = React.lazy(() => import('../billing/Billing'));

export class Payment extends React.PureComponent {
    static propTypes = {
        conversation: PropTypes.instanceOf(Conversation).isRequired,
        paymentMethods: PropTypes.arrayOf(PropTypes.instanceOf(PaymentMethod)),
        currency: PropTypes.string,
        user: PropTypes.instanceOf(User),
        finalizeBillingConversation: PropTypes.func.isRequired,
        fetchPaymentMethods: PropTypes.func.isRequired,
        fetchBillingAddress: PropTypes.func.isRequired,
        fetchLegalEntityAssignment: PropTypes.func.isRequired,
        fetchLegalEntityCountries: PropTypes.func.isRequired,
        fetchTransferts: PropTypes.func.isRequired,
        fetchUser: PropTypes.func.isRequired,
        showPaymentSuccessPopup: PropTypes.func.isRequired,
        coupon: PropTypes.shape({
            couponName: PropTypes.string
        }),
        is3xPaymentFromFinalization: PropTypes.bool, // for 3x payment
        updateIs3xPaymentFromFinalization: PropTypes.func,
        billingAddress: PropTypes.object
    };

    constructor(props) {
        super(props);

        this.backToConversation = this.backToConversation.bind(this);
        this.finalizeExchange = this.finalizeExchange.bind(this);
        props.fetchPaymentMethods();
        props.fetchBillingAddress();
        props.fetchLegalEntityAssignment();
        props.fetchLegalEntityCountries();
    }

    backToConversation() {
        document.dispatchEvent(new CustomEvent('dismiss_card'));
    }

    finalizeExchange(paymentData, paymentType, is3xPaymentFromFinalization = false, isPaymentMethod = false) {
        const { conversation, user, fetchTransferts, fetchUser, showPaymentSuccessPopup, coupon } =
            this.props;

        return this.props
            .finalizeBillingConversation(
                conversation.id,
                paymentData,
                paymentType,
                user.id,
                coupon.couponName,
                is3xPaymentFromFinalization,
                isPaymentMethod,
                user
            )
            .then(
                (response) => {
                    const shouldRedirectToInfosPanel = response.exchanges.every((exchange) =>
                        exchange.isFinalized()
                    );

                    document.dispatchEvent(
                        new CustomEvent('request_card', {
                            detail: {
                                view: shouldRedirectToInfosPanel ? 'infos' : 'details',
                                animate: true
                            }
                        })
                    );

                    // Refresh user and transfers as the user might now be a subscriber
                    Promise.all([fetchTransferts(), fetchUser()]).then(
                        () => shouldRedirectToInfosPanel && showPaymentSuccessPopup()
                    );

                    this.sendTracking(response, paymentType);
                },
                ({ payload }) => {
                    if (
                        payload &&
                        payload.error &&
                        payload.error.error &&
                        payload.error.error.details &&
                        payload.error.error.details.code
                    ) {
                        let title, text;
                        if (payload.error.error.details.code === 'host.already.have.finalized.exchanges') {
                            title = i18n.t('exchange:error.guest.finalized.exchange.title');
                            text = i18n.t('exchange:error.guest.finalized.exchange.description');
                        } else if (
                            payload.error.error.details.code === 'host.already.have.preapproved.exchanges'
                        ) {
                            title = i18n.t('exchange:error.guest.preapproved.exchange.title');
                            text = i18n.t('exchange:error.guest.preapproved.exchange.description');
                        }

                        if (title) {
                            sweetAlert({
                                title,
                                text,
                                icon: 'error'
                            });
                        }
                    }

                    const purchase = this.getPurchaseForTracking(paymentType);

                    this.sendPurchaseTracking({ ...purchase, payment_status: 'fail' });

                    return Promise.reject(payload);
                }
            );
    }

    sendTracking(response, paymentType) {
        const { conversation, user } = this.props;
        const exchangeAsGuest = conversation.getExchangeAsGuest();

        let purchaseExchange = null;
        if (response && response.exchanges && response.exchanges.length > 0) {
            purchaseExchange = response.exchanges.find((e) => e.id === exchangeAsGuest.id);
        }

        const purchase = this.getPurchaseForTracking(paymentType);

        this.sendPurchaseTracking({
            ...purchase,
            order: purchaseExchange?.get('order'),
            payment_status: 'success'
        });

        Analytics.trackGTM('MixpanelSubscriptionPurchased', { userStatus: user.getStatus() });
        Analytics.trackGTM('exchange_status', {
            event_data: {
                exchange_status: 'finalized',
                exchange_type: conversation.isMutual() ? 'reciprocal_exchange' : 'guest_point_exchange',
                collection: user.isCollection()
            }
        });
    }

    sendPurchaseTracking(purchaseObject) {
        const { user } = this.props;
        const { fetchPurchase } = useGTM(user);

        Analytics.trackGTM('purchase', fetchPurchase(purchaseObject));
    }

    getPurchaseForTracking(paymentType) {
        const { conversation, currency, user, coupon, is3xPaymentFromFinalization } = this.props;
        const exchangeAsGuest = conversation.getExchangeAsGuest();
        const total = exchangeAsGuest.getTotalToPay(false, user.isSubscriber());

        return {
            currency,
            price: total,
            coupon,
            type: paymentType === 'credit-card' || paymentType === 'saved-card' ? 'card' : 'paypal',
            is3xPayment: is3xPaymentFromFinalization,
            product: exchangeAsGuest.getEcommerceProducts(user.isSubscriber())
        };
    }

    render() {
        const { conversation, currency, paymentMethods, user, billingAddress } = this.props;
        const exchangeAsGuest = conversation.getExchangeAsGuest();
        if (!conversation || !user || !currency || paymentMethods === null || billingAddress === undefined) {
            return <Loading />;
        }

        return (
            <div className="container">
                <Suspense fallback={<div>Loading...</div>}>
                    <Billing
                        backButton={{
                            text: i18n.t('subscription:back_to_conversation'),
                            action: this.backToConversation
                        }}
                        context="messaging"
                        currency={currency}
                        products={exchangeAsGuest.getProductsToPay(user.isSubscriber())}
                        paymentMethods={paymentMethods}
                        totalToPay={exchangeAsGuest.getTotalToPay(false, user.isSubscriber())}
                        payAction={this.finalizeExchange}
                        is3xPaymentFromFinalization={this.props.is3xPaymentFromFinalization}
                        updateIs3xPaymentFromFinalization={this.props.updateIs3xPaymentFromFinalization}
                        needToBuyGp={conversation ? conversation.hasToBuyGP() : null}
                        source={PaymentModel.FINALIZATION_PAYMENT_SOURCE}
                        sourceId={conversation.id}
                    />
                </Suspense>
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    paymentMethods: state.paymentMethods.data ? state.paymentMethods.data : [],
    user: state.user,
    coupon: state.subscription.coupon,
    billingAddress: state.billingAddress.billingAddress
});

const mapDispatchToProps = (dispatch) => ({
    finalizeBillingConversation: bindActionCreators(finalizeBillingConversation, dispatch),
    fetchTransferts: bindActionCreators(fetchTransfertsAction, dispatch),
    fetchUser: bindActionCreators(fetchUserAction, dispatch),
    fetchPaymentMethods: bindActionCreators(fetchPaymentMethods, dispatch),
    fetchBillingAddress: bindActionCreators(fetchBillingAddress, dispatch),
    fetchLegalEntityAssignment: bindActionCreators(fetchLegalEntityAssignment, dispatch),
    fetchLegalEntityCountries: bindActionCreators(fetchLegalEntityCountries, dispatch)
});

export default compose(withCurrency, connect(mapStateToProps, mapDispatchToProps))(Payment);
